<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-cog </v-icon>
        <span class="px-2">{{$t('settings')}}</span>
      </v-card-title>
      <v-card-text>
        <vue-element-loading :active="isLoading" />
        <v-form ref="form" v-if="settings" v-model="valid" lazy-validation class="my-2">
          <v-row>
            <v-col cols="12" md="3" class="my-2">
              <v-autocomplete
                v-model="selectedCurrency"
                :items="currencies"
                dense
                outlined
                item-text="name"
                item-value="id"
                :label="$t('currency')"
                :filter="customFilter"
                persistent-hint
                return-object
                required
                :rules="requiredRules"
                :hint="
                  selectedCurrency != null
                    ? selectedCurrency.code + ' (' + selectedCurrency.symbol + ')'
                    : ''
                "
                @change="currencySelected"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.base_price"
                  type="number"
                  outlined
                  dense
                  :label="$t('base_price')"
                  :placeholder="$t('enter_base_price')"
                  persistent-hint
                  required
                  :rules="numberRulesZeroAllowed"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.rate_per_km"
                  type="number"
                  outlined
                  dense
                  :label="$t('rate_per_km')"
                  :placeholder="$t('enter_rate_per_km')"
                  persistent-hint
                  required
                  :rules="numberRulesZeroAllowed"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.commission"
                  type="number"
                  outlined
                  dense
                  :label="$t('commission_percent')"
                  :placeholder="$t('enter_commission_percentage')"
                  persistent-hint
                  required
                  :rules="percentRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.max_reservations_per_trip"
                  type="number"
                  outlined
                  dense
                  :label="$t('max_reservations_per_trip')"
                  :placeholder="$t('enter_max_reservations')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.publish_trips_future_days"
                  type="number"
                  outlined
                  dense
                  :label="$t('publish_trips_future_days')"
                  :placeholder="$t('enter_days_future')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <!-- max_distance_to_stop -->
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.max_distance_to_stop"
                  type="number"
                  outlined
                  dense
                  :label="$t('max_distance_to_stop')"
                  :placeholder="$t('enter_max_distance')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <!-- distance_to_stop_to_mark_arrived -->
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_stop_to_mark_arrived"
                  type="number"
                  outlined
                  dense
                  :label="$t('distance_to_mark_arrived')"
                  :placeholder="$t('enter_distance_arrived')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('trips_and_drivers_settings') }}</label>
          </div>
          <v-row>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.max_driver_trips_per_day"
                  type="number"
                  outlined
                  dense
                  :label="$t('max_driver_trips_per_day')"
                  :placeholder="$t('enter_max_driver_trips')"
                  persistent-hint
                  required
                  :rules="maxDriverTripsRules"
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('bus_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.allow_seat_selection"
                :label="$t('allow_seat_selection')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('security_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.otp_required"
                :label="$t('allow_otp')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('ads_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.allow_ads_in_driver_app"
                :label="$t('allow_ads_driver_app')"
                persistent-hint
              ></v-checkbox>
              <v-checkbox
                v-model="settings.allow_ads_in_customer_app"
                :label="$t('allow_ads_customer_app')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>

        </v-form>
      </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn :disabled="!valid" color="primary" @click="saveSettings">
            {{$t('save')}}
            <v-icon right dark> mdi-content-save </v-icon>
          </v-btn>
        </v-card-actions>
    </v-card>
  </div>
</template>

<script>

import VueElementLoading from "vue-element-loading";

export default {
  components: {
    VueElementLoading,
  },
  data() {
    return {
      currencies: [],
      isLoading: false,
      settings: null,
      valid: true,
      requiredRules: [(v) => !!v || this.$t("required")],
      numberRules: [(v) => /^(0*[1-9][0-9]*(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/.test(v) || this.$t("must_be_greater_than_0")],
      numberRulesZeroAllowed: [(v) => /^(0*[0-9]+(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/.test(v) || this.$t("must_be_0_or_greater")],
      percentRules: [(v) => /^100$|^[0-9]{1,2}$|^[0-9]{1,2}\,[0-9]{1,3}$/.test(v) || this.$t("must_be_0_to_100")],
      maxDriverTripsRules: [
        (v) => !!v || this.$t("required"),
        (v) => (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 50) || 'يجب أن تكون القيمة بين 1 و 50'
      ],

      selectedCurrency: null,
    };
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    customFilter (item, queryText, itemText) {
      const textOne = item.name.toLowerCase()
      const textTwo = item.code.toLowerCase()
      const searchText = queryText.toLowerCase()

      return textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1
    },
    //API Calls
    saveSettings() {
      this.submiting = true;
      axios
        .post("/settings/update", this.settings)
        .then((response) => {
          this.submiting = false;
          this.$notify({
            title: "Success",
            text: this.$t("settings_updated"),
            type: "success",
          });
        })
        .catch((error) => {
          this.submiting = false;
          this.$notify({
            title: "Error",
            text: this.$t("error_updating_settings"),
            type: "error",
          });
          console.log(error);
        });
    },
    loadSettings() {
      this.isLoading = true;
      this.settings = [];
      axios
        .get(`/settings/all`)
        .then((response) => {
          this.settings = response.data;
          this.loadCurrencies();
          this.selectedCurrency = this.settings.currency;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_settings"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    loadCurrencies() {
      this.isLoading = true;
      this.currencies = [];
      axios
        .get(`/currencies/all`)
        .then((response) => {
          this.currencies = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_currencies"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate() {
      this.valid = false;
      let v = this.$refs.form.validate();
      if (v) {
        this.valid = true;
        return true;
      }
      return false;
    },
    currencySelected(r) {
      this.selectedCurrency = r;
      this.settings.currency_id = this.selectedCurrency.id;
    },
  },
};
</script>
